import { db, zoneList, zoneById } from "./db";
import { parsePosix } from "./posix";

/** Explicit picks for ambiguous or very common abbreviations. */
const PREFERRED: Record<string, string> = {
  UTC: "UTC",
  GMT: "Europe/London",
  BST: "Europe/London",
  IST: "Asia/Kolkata",
  CST: "America/Chicago",
  CDT: "America/Chicago",
  EST: "America/New_York",
  EDT: "America/New_York",
  MST: "America/Denver",
  MDT: "America/Denver",
  PST: "America/Los_Angeles",
  PDT: "America/Los_Angeles",
  AST: "America/Halifax",
  ADT: "America/Halifax",
  CET: "Europe/Berlin",
  CEST: "Europe/Berlin",
  EET: "Europe/Athens",
  EEST: "Europe/Athens",
  WET: "Europe/Lisbon",
  WEST: "Europe/Lisbon",
  AEST: "Australia/Sydney",
  AEDT: "Australia/Sydney",
  ACST: "Australia/Adelaide",
  ACDT: "Australia/Adelaide",
};

/** Zones that win ties for any other abbreviation they use. */
const MAJOR = new Set([
  "America/Anchorage", "America/Denver", "America/Chicago", "America/New_York", "America/Los_Angeles", "America/Halifax", "America/St_Johns",
  "Pacific/Honolulu", "Europe/London", "Europe/Dublin", "Europe/Berlin", "Europe/Athens", "Europe/Lisbon", "Europe/Moscow", "Europe/Istanbul",
  "Africa/Lagos", "Africa/Johannesburg", "Africa/Nairobi", "Africa/Maputo", "Asia/Jerusalem", "Asia/Karachi", "Asia/Kolkata", "Asia/Jakarta",
  "Asia/Makassar", "Asia/Jayapura", "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul", "Asia/Manila", "Australia/Perth",
  "Australia/Adelaide", "Australia/Sydney", "Pacific/Auckland", "Pacific/Guam", "Pacific/Pago_Pago",
]);

const ALPHA = /^[A-Za-z]{2,6}$/;

/** Abbreviations each zone has used recently or will use: zone id -> abbrs. */
export const zoneAbbrs: Map<string, string[]> = (() => {
  const now = Math.floor(Date.now() / 1000);
  const from = now - 10 * 366 * 86400;
  const to = now + 3 * 366 * 86400;
  const m = new Map<string, string[]>();
  for (const info of zoneList) {
    const z = db.zone(info.id);
    const set = new Set<string>();
    const add = (a: string) => {
      if (ALPHA.test(a) && a !== "LMT") set.add(a.toUpperCase());
    };
    add(z.typeAt(from).abbr);
    for (const tr of z.transitionsBetween(from, to)) add(tr.after.abbr);
    const raw = db.raw(info.id);
    const posix = raw.f ? parsePosix(raw.f) : null;
    if (posix) {
      add(posix.std.abbr);
      if (posix.dst) add(posix.dst.abbr);
    }
    if (set.size) m.set(info.id, [...set]);
  }
  return m;
})();

const byAbbr: Map<string, string[]> = (() => {
  const m = new Map<string, string[]>();
  for (const [id, abbrs] of zoneAbbrs) for (const a of abbrs) (m.get(a) ?? m.set(a, []).get(a)!).push(id);
  for (const [a, ids] of m) {
    ids.sort((x, y) => {
      const px = PREFERRED[a] === x ? 2 : MAJOR.has(x) ? 1 : 0;
      const py = PREFERRED[a] === y ? 2 : MAJOR.has(y) ? 1 : 0;
      if (px !== py) return py - px;
      const cx = zoneById.get(x)?.countries.length ?? 0;
      const cy = zoneById.get(y)?.countries.length ?? 0;
      return cy - cx || x.localeCompare(y);
    });
  }
  return m;
})();

/** All zones using an abbreviation, best match first. */
export function zonesForAbbr(abbr: string): string[] {
  return byAbbr.get(abbr.toUpperCase()) ?? [];
}

const lowerIds: Map<string, string> = (() => {
  const m = new Map<string, string>();
  for (const id of db.ids()) m.set(id.toLowerCase(), id);
  for (const id of db.linkIds()) m.set(id.toLowerCase(), db.canonical(id)!);
  return m;
})();

/**
 * Resolve user/URL input to a canonical zone id: an id in any case
 * ("europe/berlin"), an abbreviation ("CEST", "aest"), or a whole-hour offset
 * ("UTC+9", "GMT-5", "+0200"). Returns null if nothing matches.
 */
export function resolveZone(input: string): string | null {
  const s = input.trim();
  if (!s) return null;
  // Curated abbreviations win over tzdata's legacy ids of the same name
  // ("EST" links to America/Panama, "CET" to Europe/Brussels).
  const preferred = PREFERRED[s.toUpperCase()];
  if (preferred) return preferred;
  const exact = db.canonical(s);
  if (exact) return exact;
  const byCase = lowerIds.get(s.toLowerCase().replace(/ /g, "_"));
  if (byCase) return byCase;
  const abbr = byAbbr.get(s.toUpperCase());
  if (abbr) return abbr[0];
  // In a URL query "+" arrives as a space ("UTC 9"), so a bare space means plus.
  const m = /^(?:UTC|GMT|Z)?\s*([+-]|\s)\s*(\d{1,2})(?::?(\d{2}))?$/i.exec(s);
  if (m) {
    const hours = +m[2];
    const minutes = +(m[3] ?? 0);
    if (minutes === 0 && hours <= 14) {
      if (hours === 0) return "UTC";
      // Etc/GMT signs are inverted: Etc/GMT-9 is UTC+9.
      const etc = `Etc/GMT${m[1] === "-" ? "+" : "-"}${hours}`;
      return db.canonical(etc);
    }
  }
  return null;
}
